/**
 * @fileoverview API calls for Task Tracker (AR-513).
 *
 * O backend usa nomes baseados em `session_meeting` e `tutor`; no front
 * preferimos `lesson` e `teacher`. O rename é feito aqui, ao receber a
 * resposta — componentes consomem só os tipos de domínio.
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import { resolveTrackStatus } from '../utils/aggregateLessonStatus'
import type {
  StudentActivityStatus,
  TaskTrackActivity,
  TaskTrackCellDelivery,
  TaskTrackDeliverable,
  TaskTrackGroup,
  TaskTrackLesson,
  TaskTrackMatrix,
  TaskTrackProject,
  TaskTrackStudent,
  TaskTrackStudentRow,
  TaskTrackSummary,
  TrackStatus,
} from '../types'

// Backend response — escopo local ao service.
interface MatrixApiResponse {
  summary: {
    studentsSelfAssessmentsPending: { pending: number; total: number }
    tutorAssessmentsPending: { pending: number; total: number }
    studentsDeliverablesPending: { pending: number; total: number }
  }
  activities: Array<{
    id: string
    title: string
    sessionMeetingId: string
    sessionMeetingTitle: string
    sessionMeetingDate: string
    hasAssessments: boolean
    hasTasks: boolean
  }>
  finalAssessment: {
    enabled: boolean
    present: boolean
  }
  processing: boolean
  students: Array<{
    id: string
    firstName: string
    lastName: string
    avatarUrl: string
    processing: boolean
    activities: Array<{
      id: string
      assessmentId: string | null
      selfAssessmentCompleted: boolean
      tutorAssessmentCompleted: boolean
      deliverableCompleted: boolean
      /** Additive field (AR-708) — absent in older payloads. */
      selfAssessmentStatus?: TrackStatus
      /** Additive field (AR-708) — absent in older payloads. */
      tutorAssessmentStatus?: TrackStatus
      /** Additive field (AR-924) — absent in older payloads. */
      deliverable?: TaskTrackCellDelivery | null
    }>
    finalAssessment: {
      id: string | null
      selfAssessmentCompleted: boolean
      tutorAssessmentCompleted: boolean
      /** Additive field (AR-708) — absent in older payloads. */
      selfAssessmentStatus?: TrackStatus
      /** Additive field (AR-708) — absent in older payloads. */
      tutorAssessmentStatus?: TrackStatus
    }
  }>
}

function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

function fullName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim()
}

function adaptSummary(raw: MatrixApiResponse['summary']): TaskTrackSummary {
  return {
    selfAssessments: {
      pending: raw.studentsSelfAssessmentsPending.pending,
      completed: raw.studentsSelfAssessmentsPending.total - raw.studentsSelfAssessmentsPending.pending,
      total: raw.studentsSelfAssessmentsPending.total,
    },
    teacherAssessments: {
      pending: raw.tutorAssessmentsPending.pending,
      completed: raw.tutorAssessmentsPending.total - raw.tutorAssessmentsPending.pending,
      total: raw.tutorAssessmentsPending.total,
    },
    deliverables: {
      pending: raw.studentsDeliverablesPending.pending,
      completed: raw.studentsDeliverablesPending.total - raw.studentsDeliverablesPending.pending,
      total: raw.studentsDeliverablesPending.total,
    },
  }
}

function adaptActivity(raw: MatrixApiResponse['activities'][number]): TaskTrackActivity {
  return {
    id: raw.id,
    title: raw.title,
    lessonId: raw.sessionMeetingId,
    lessonTitle: raw.sessionMeetingTitle,
    lessonDate: raw.sessionMeetingDate,
    hasAssessments: raw.hasAssessments,
    hasDeliverables: raw.hasTasks,
  }
}

function adaptStudentRow(raw: MatrixApiResponse['students'][number]): TaskTrackStudentRow {
  return {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    fullName: fullName(raw.firstName, raw.lastName),
    avatarUrl: raw.avatarUrl ?? '',
    processing: raw.processing ?? false,
    activities: raw.activities.map(
      (act): StudentActivityStatus => ({
        activityId: act.id,
        assessmentId: act.assessmentId,
        selfAssessmentCompleted: act.selfAssessmentCompleted,
        teacherAssessmentCompleted: act.tutorAssessmentCompleted,
        deliverableCompleted: act.deliverableCompleted,
        deliverable: act.deliverable ?? null,
        selfAssessmentStatus: resolveTrackStatus(act.selfAssessmentStatus, act.selfAssessmentCompleted),
        teacherAssessmentStatus: resolveTrackStatus(act.tutorAssessmentStatus, act.tutorAssessmentCompleted),
      }),
    ),
    finalAssessment: {
      id: raw.finalAssessment.id,
      selfAssessmentCompleted: raw.finalAssessment.selfAssessmentCompleted,
      teacherAssessmentCompleted: raw.finalAssessment.tutorAssessmentCompleted,
      selfAssessmentStatus: resolveTrackStatus(
        raw.finalAssessment.selfAssessmentStatus,
        raw.finalAssessment.selfAssessmentCompleted,
      ),
      teacherAssessmentStatus: resolveTrackStatus(
        raw.finalAssessment.tutorAssessmentStatus,
        raw.finalAssessment.tutorAssessmentCompleted,
      ),
    },
  }
}

export async function fetchGroups(): Promise<TaskTrackGroup[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.groups)
  assertAuthenticated(response)
  const data = response.data as { groups: TaskTrackGroup[] }
  return data.groups ?? []
}

export async function fetchProjectsByGroup(groupId: string): Promise<TaskTrackProject[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.projectsByClass(groupId))
  assertAuthenticated(response)
  const data = response.data as { projects: TaskTrackProject[] }
  return data.projects ?? []
}

export async function fetchStudentsByGroup(groupId: string, projectId?: string): Promise<TaskTrackStudent[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.studentsByClass(groupId), {
    params: projectId ? { projectId } : undefined,
  })
  assertAuthenticated(response)
  const data = response.data as { students: Array<{ id: string; firstName: string; lastName: string }> }
  return (data.students ?? []).map(s => ({ ...s, fullName: fullName(s.firstName, s.lastName) }))
}

export async function fetchTaskTrackMatrix(projectId: string, studentId?: string): Promise<TaskTrackMatrix> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.matrix, {
    params: { projectId, ...(studentId && { studentId }) },
  })
  assertAuthenticated(response)
  const data = response.data as MatrixApiResponse
  return {
    summary: adaptSummary(data.summary),
    activities: data.activities.map(adaptActivity),
    students: data.students.map(adaptStudentRow),
    finalAssessment: {
      enabled: data.finalAssessment?.enabled ?? false,
      present: data.finalAssessment?.present ?? false,
    },
    processing: data.processing ?? false,
  }
}

/**
 * Groups a matrix's activities by lesson. Preserves activity ordering from the
 * API (lesson date asc, sort_position asc), so the lessons[] order is also the
 * order in which lessons should be rendered as columns.
 *
 * Used by the new lesson-grouped Tasks Tracker grid (AR-539).
 */
export function groupActivitiesByLesson(activities: TaskTrackActivity[]): TaskTrackLesson[] {
  const lessons: TaskTrackLesson[] = []
  const byId = new Map<string, TaskTrackLesson>()
  for (const activity of activities) {
    let lesson = byId.get(activity.lessonId)
    if (!lesson) {
      lesson = {
        id: activity.lessonId,
        title: activity.lessonTitle,
        date: activity.lessonDate,
        hasAssessments: false,
        deliverableActivities: [],
      }
      byId.set(activity.lessonId, lesson)
      lessons.push(lesson)
    }
    if (activity.hasAssessments) lesson.hasAssessments = true
    if (activity.hasDeliverables) lesson.deliverableActivities.push(activity)
  }
  return lessons
}

/** AR-924: cells whose done delivery was uploaded by a teacher (summary "Uploaded by teacher"). */
export function countTeacherUploads(matrix: TaskTrackMatrix): number {
  return matrix.students.reduce(
    (total, student) =>
      total +
      student.activities.filter(cell => cell.deliverableCompleted && cell.deliverable?.uploadedByTeacher).length,
    0,
  )
}

export async function fetchDeliverable(
  sessionMeetingActivityId: string,
  studentId: string,
): Promise<TaskTrackDeliverable> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.deliverable, {
    params: { sessionMeetingActivityId, studentId },
  })
  assertAuthenticated(response)
  return response.data as TaskTrackDeliverable
}
